import BigNumber from 'bignumber.js';
import { erc20Abi } from 'libs/contracts';
import type { Token, TokenBalance } from 'types';
import type { Address, PublicClient } from 'viem';

type MulticallResult<T> =
  | {
      status: 'success';
      result: T;
    }
  | {
      status: 'failure';
    };

export interface GetTokenBalancesInput {
  publicClient: PublicClient;
  accountAddress: Address;
  tokens: Token[];
}

export type GetTokenBalancesOutput = {
  tokenBalances: TokenBalance[];
};

export const getTokenBalances = async ({
  publicClient,
  accountAddress,
  tokens,
}: GetTokenBalancesInput): Promise<GetTokenBalancesOutput> => {
  const tokenBalanceMantissas: (bigint | undefined)[] = new Array(tokens.length);
  const erc20TokenIndexes: number[] = [];
  const nativeTokenIndexes: number[] = [];

  tokens.forEach((token, index) => {
    if (token.isNative) {
      nativeTokenIndexes.push(index);
    } else {
      erc20TokenIndexes.push(index);
    }
  });

  const [erc20TokenBalanceResults, nativeTokenBalanceResults] = await Promise.all([
    erc20TokenIndexes.length > 0
      ? (publicClient.multicall({
          contracts: erc20TokenIndexes.map((index) => ({
            abi: erc20Abi,
            address: tokens[index]!.address,
            functionName: 'balanceOf',
            args: [accountAddress],
          })),
        }) as Promise<MulticallResult<bigint>[]>)
      : Promise.resolve([]),
    Promise.allSettled(
      nativeTokenIndexes.map(() =>
        publicClient.getBalance({
          address: accountAddress,
        }),
      ),
    ),
  ]);

  erc20TokenBalanceResults.forEach((tokenBalanceResult, resultIndex) => {
    if (tokenBalanceResult.status === 'success') {
      tokenBalanceMantissas[erc20TokenIndexes[resultIndex]!] = tokenBalanceResult.result;
    }
  });

  nativeTokenBalanceResults.forEach((tokenBalanceResult, resultIndex) => {
    if (tokenBalanceResult.status === 'fulfilled') {
      tokenBalanceMantissas[nativeTokenIndexes[resultIndex]!] = tokenBalanceResult.value;
    }
  });

  const tokenBalances = tokenBalanceMantissas.flatMap<TokenBalance>(
    (balanceMantissaValue, index) => {
      const token = tokens[index];
      if (!token) return [];

      const balanceMantissa =
        balanceMantissaValue !== undefined
          ? new BigNumber(balanceMantissaValue.toString())
          : undefined;

      if (!balanceMantissa) return [];

      return [{ token, balanceMantissa }];
    },
  );

  return { tokenBalances };
};
